import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import {
  Button,
  IconSparkle,
  ScreenLayout,
  Skeleton,
  SkeletonAvatar,
  SkeletonBlock,
  SkeletonButton,
  SkeletonImage,
  SkeletonListRows,
  SkeletonStats,
  SkeletonText,
  ViewHost,
  useLoadingState,
  useScreen,
} from '@taki/ui';
import {
  getDemoIntervention,
  listDemoInterventions,
  type DemoIntervention,
} from './demo-data.js';
import {
  DEMO_APP_ID,
  DEMO_BINDINGS,
  DEMO_FILTERS,
  DEMO_SCREEN_ID,
  DEMO_VIEW_TYPES,
} from './demo-views.js';

/**
 * Ecran de l'app `ui-lab`, VITRINE du socle @taki/ui.
 *
 * App de demonstration : aucun SDK, aucune ecriture reelle. Le menu (zone 1)
 * segmente la vitrine en cinq sections, dans le MEME ScreenLayout :
 *  - Vues      : les 5 vues natives du core + une 6e vue CUSTOM locale a l'app,
 *                nourries par un jeu de donnees en memoire (demo-data.ts) ;
 *  - Primitifs : chaque brique skeleton (SkeletonBlock/Text/Avatar/Button/Image) ;
 *  - Composes  : SkeletonListRows, SkeletonStats et le wrapper Skeleton ;
 *  - Etats     : demo interactive de useLoadingState (delai, duree minimale,
 *                timeout) et du Button `loading` ;
 *  - Motion    : shimmer contre pulse, spinner, note prefers-reduced-motion.
 *
 * LA SECTION APPARTIENT AU SOCLE (`screen.section` / `screen.setSection`,
 * portee par `query.section`) : elle vit dans l'adresse (`?s=motion`), donc
 * dans un lien partageable, et le bouton Precedent du navigateur refait le
 * chemin entre les demos. L'ecran n'en tient plus de `useState`, et il n'a
 * aucun effet de rechargement a ecrire : la section fait partie de la cle de
 * chargement du core, qui rappelle le chargeur tout seul quand elle change.
 * PARTICULARITE DE CETTE VITRINE, a lire avant de la comparer au modele du
 * gabarit (`apps/hello/src/apps/inventaire/InventaireScreen.tsx`) : sa section
 * ne SEGMENTE pas une collection, elle change le SUJET de la page. Une seule
 * des cinq entrees montre des donnees, les quatre autres mettent en scene des
 * primitifs qui ne chargent rien. Le chargeur n'a donc rien a lire dans
 * `query.section` (le pourquoi est detaille sur `load`).
 *
 * Regles de la cage IA (AGENTS.md + docs/GABARIT-ECRAN.md) : squelette 100% via
 * ScreenLayout, UI 100% via @taki/ui, contenu de la zone 5 a plat separe par
 * des filets (jamais de Card), motion via les tokens du socle uniquement.
 */

/** Les sections de la vitrine (segmentation du menu, zone 1). */
type LabSection = 'vues' | 'primitives' | 'composes' | 'etats' | 'motion';

/**
 * LE catalogue des sections, et le seul : le core n'en connait pas la liste
 * (il ne transporte qu'une chaine dans `query.section`). C'est donc lui qui
 * dessine le menu, qui filtre ce que `setSection` a le droit d'ecrire dans la
 * requete, et qui resout la section portee par l'adresse.
 */
const MENU_ITEMS: { id: LabSection; label: string }[] = [
  { id: 'vues', label: 'Vues' },
  { id: 'primitives', label: 'Primitifs' },
  { id: 'composes', label: 'Composes' },
  { id: 'etats', label: 'Etats' },
  { id: 'motion', label: 'Motion' },
];

/* ------------------------------------------------------------------ */
/* Brique de mise en page : une demo a plat, separee par un filet.     */
/* ------------------------------------------------------------------ */

/**
 * Une entree de la vitrine : nom du primitif en overline, note explicative,
 * puis le rendu de demonstration. A plat, pleine largeur, filet en bas
 * (pattern de la zone 5, jamais de Card).
 */
function Demo({
  name,
  note,
  children,
}: {
  name: string;
  note: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="border-b border-border-soft px-5 py-4">
      <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-300">
        {name}
      </p>
      <p className="mt-1 max-w-2xl text-[0.8125rem] text-ink-500">{note}</p>
      <div className="mt-3.5">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section Primitifs                                                    */
/* ------------------------------------------------------------------ */

function PrimitivesSection(): JSX.Element {
  return (
    <div>
      <Demo
        name="SkeletonBlock"
        note="Bloc rectangulaire generique : la brique de base pour composer un skeleton. Largeur, hauteur et rayon libres."
      >
        <div className="flex flex-col gap-2">
          <SkeletonBlock w="12rem" h="1rem" />
          <SkeletonBlock w="8rem" h="1.5rem" rounded="var(--radius-md)" />
          <SkeletonBlock w="100%" h="0.75rem" />
        </div>
      </Demo>
      <Demo
        name="SkeletonText"
        note="Paragraphe factice : lignes de largeur variable deterministe, derniere ligne plus courte."
      >
        <div className="max-w-md">
          <SkeletonText lines={4} />
        </div>
      </Demo>
      <Demo
        name="SkeletonAvatar"
        note="Rond a la place d'un Avatar, meme convention de taille (px de maquette rendus en rem)."
      >
        <div className="flex items-center gap-4">
          <SkeletonAvatar size={24} />
          <SkeletonAvatar size={30} />
          <SkeletonAvatar size={44} />
        </div>
      </Demo>
      <Demo
        name="SkeletonButton"
        note="Empreinte d'un bouton md (hauteur et rayon d'un Button), pour reserver la place d'une action."
      >
        <div className="flex items-center gap-3">
          <SkeletonButton />
          <SkeletonButton w="9rem" />
        </div>
      </Demo>
      <Demo name="SkeletonImage" note="Bloc a la place d'une image ou d'une vignette.">
        <div className="max-w-sm">
          <SkeletonImage h="9rem" />
        </div>
      </Demo>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section Composes                                                     */
/* ------------------------------------------------------------------ */

/** Demo du wrapper declaratif <Skeleton loading skeleton>. */
function WrapperDemo(): JSX.Element {
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    },
    [],
  );

  const replay = (): void => {
    if (timerRef.current != null) clearTimeout(timerRef.current);
    setLoading(true);
    timerRef.current = setTimeout(() => setLoading(false), 1200);
  };

  return (
    <div className="flex flex-col gap-3.5">
      <div className="max-w-md">
        <Skeleton loading={loading} skeleton={<SkeletonText lines={3} />}>
          <p className="text-sm text-ink-700">
            Contenu reel. Il monte avec un fondu (.taki-anim-fade) au moment
            precis ou le skeleton disparait : le swap ne clignote pas.
          </p>
        </Skeleton>
      </div>
      <div>
        <Button variant="secondary" size="sm" onClick={replay} disabled={loading}>
          Rejouer le chargement
        </Button>
      </div>
    </div>
  );
}

function ComposesSection(): JSX.Element {
  return (
    <div>
      <Demo
        name="SkeletonListRows"
        note="Copie exacte de la ligne de liste de la zone 5 (avatar 30 + nom + sous-ligne + filet). C'est le skeleton PAR DEFAUT du gabarit pour l'etat chargement : zero saut au swap."
      >
        <SkeletonListRows rows={3} />
      </Demo>
      <Demo
        name="SkeletonListRows withAvatar={false}"
        note="La meme ligne sans avatar, pour les listes sans photo : evite le saut horizontal quand la vraie ligne n'a pas d'avatar."
      >
        <SkeletonListRows rows={3} withAvatar={false} />
      </Demo>
      <Demo
        name="SkeletonStats"
        note="Stats d'en-tete factices (zone 3) : valeur + libelle alignes a droite, separes par un filet vertical."
      >
        <div className="flex justify-end">
          <SkeletonStats count={3} />
        </div>
      </Demo>
      <Demo
        name="Skeleton (wrapper)"
        note="Rend le skeleton pendant le chargement, puis le contenu reel avec un fondu au swap. Une seule prop loading a piloter."
      >
        <WrapperDemo />
      </Demo>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section Etats (demo interactive de useLoadingState + Button loading) */
/* ------------------------------------------------------------------ */

/** Un scenario simule pour la demo du hook. */
type Scenario = 'idle' | 'fast' | 'slow' | 'timeout';

/** Booleen d'etat, rendu lisible (true en encre forte, false attenue). */
function StateFlag({ on }: { on: boolean }): JSX.Element {
  return (
    <span className={on ? 'font-semibold text-ink-900' : 'text-ink-400'}>
      {on ? 'true' : 'false'}
    </span>
  );
}

/**
 * Demo du hook useLoadingState : delai 300 ms avant affichage, duree minimale
 * 400 ms, timeout 10 s vers l'etat erreur. On observe showLoading et timedOut
 * evoluer selon le scenario simule (memes reglages que le gabarit).
 */
function LoadingStateDemo(): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [scenario, setScenario] = useState<Scenario>('idle');
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { showLoading, timedOut } = useLoadingState(loading);

  // Chrono d'affichage : rend le delai de 300 ms et le timeout de 10 s
  // observables (s'arrete et se remet a zero des que le chargement finit).
  useEffect(() => {
    if (!loading) {
      setElapsed(0);
      return undefined;
    }
    const start = Date.now();
    const id = setInterval(() => setElapsed(Date.now() - start), 100);
    return () => clearInterval(id);
  }, [loading]);

  useEffect(
    () => () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    },
    [],
  );

  const clearTimer = (): void => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // ms = null : le chargement ne se resout jamais (scenario timeout).
  const run = (next: Scenario, ms: number | null): void => {
    clearTimer();
    setScenario(next);
    setLoading(true);
    if (ms != null) timerRef.current = setTimeout(() => setLoading(false), ms);
  };

  const reset = (): void => {
    clearTimer();
    setLoading(false);
    setScenario('idle');
  };

  // Apercu : meme priorite que la zone 5 du gabarit (timeout > skeleton > contenu).
  let preview: ReactNode;
  if (loading && timedOut) {
    preview = (
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm font-semibold text-ink-900">
          Still loading, something may be wrong
        </p>
        <Button variant="secondary" size="sm" onClick={reset}>
          Reessayer
        </Button>
      </div>
    );
  } else if (loading && showLoading) {
    preview = <SkeletonText lines={3} />;
  } else {
    preview = (
      <p className="text-sm text-ink-700">
        {scenario === 'fast'
          ? "Contenu pret. Reponse arrivee avant le delai de 300 ms : aucun skeleton ne s'est affiche (pas de flash)."
          : scenario === 'slow'
            ? 'Contenu pret. Le skeleton est reste visible sa duree minimale avant de laisser la place.'
            : "Lance un scenario pour voir le hook a l'oeuvre."}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => run('fast', 200)}
          disabled={loading}
        >
          Reponse rapide (200 ms)
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => run('slow', 1500)}
          disabled={loading}
        >
          Reponse lente (1,5 s)
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => run('timeout', null)}
          disabled={loading}
        >
          Ne repond jamais (timeout 10 s)
        </Button>
        {loading && (
          <Button variant="ghost" size="sm" onClick={reset}>
            Annuler
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-500">
        <span>
          showLoading : <StateFlag on={showLoading} />
        </span>
        <span>
          timedOut : <StateFlag on={timedOut} />
        </span>
        <span>chrono : {(elapsed / 1000).toFixed(1)} s</span>
      </div>

      <div className="max-w-md border-t border-border-soft pt-3.5">{preview}</div>
    </div>
  );
}

/**
 * Demo du Button `loading` : spinner inline a la place du contenu, bouton
 * desactive, largeur strictement stable (doctrine : spinner = action ponctuelle).
 */
function ButtonLoadingDemo(): JSX.Element {
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    },
    [],
  );

  const run = (): void => {
    if (timerRef.current != null) clearTimeout(timerRef.current);
    setLoading(true);
    timerRef.current = setTimeout(() => setLoading(false), 1500);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button variant="primary" loading={loading} onClick={run}>
        Enregistrer
      </Button>
      <Button variant="secondary" loading={loading} onClick={run}>
        Enregistrer
      </Button>
      <span className="text-xs text-ink-400">
        Spinner inline, largeur stable, bouton desactive pendant l'action.
      </span>
    </div>
  );
}

function EtatsSection(): JSX.Element {
  return (
    <div>
      <Demo
        name="useLoadingState"
        note="Transforme un booleen loading brut en affichage poli : delai 300 ms avant le skeleton (donnee rapide = aucun flash), duree minimale 400 ms une fois affiche (pas de clignotement), timeout 10 s vers l'etat erreur. Memes reglages que le gabarit."
      >
        <LoadingStateDemo />
      </Demo>
      <Demo
        name="Button loading"
        note="Doctrine : le spinner signale une action ponctuelle, jamais un chargement de contenu. Clique pour lancer une action simulee de 1,5 s."
      >
        <ButtonLoadingDemo />
      </Demo>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section Motion                                                       */
/* ------------------------------------------------------------------ */

function MotionSection(): JSX.Element {
  return (
    <div>
      <Demo
        name="Shimmer (defaut)"
        note="Reflet clair qui balaye de gauche a droite (background-position). Animation par defaut de .taki-skeleton et de tous les Skeleton*."
      >
        <div className="flex max-w-md flex-col gap-2">
          <SkeletonBlock w="100%" h="1rem" />
          <SkeletonBlock w="70%" h="1rem" />
        </div>
      </Demo>
      <Demo
        name="Pulse (variante)"
        note="Opacite douce a la place du reflet (classe .taki-skeleton-pulse). Preferable sur une grande surface, ou le balayage serait trop appuye."
      >
        <div className="flex max-w-md flex-col gap-2">
          <SkeletonBlock className="taki-skeleton-pulse" w="100%" h="1rem" />
          <SkeletonBlock className="taki-skeleton-pulse" w="70%" h="1rem" />
        </div>
      </Demo>
      <Demo
        name="Spinner"
        note="Anneau .taki-spinner (1em, currentColor) : il suit la taille du texte courant. Reserve aux actions ponctuelles (Button loading, submit), jamais au chargement de contenu."
      >
        <div className="flex items-center gap-5 text-ink-600">
          <span className="text-sm">
            <span className="taki-spinner" />
          </span>
          <span className="text-2xl">
            <span className="taki-spinner" />
          </span>
        </div>
      </Demo>
      <Demo
        name="prefers-reduced-motion"
        note="Si le systeme demande moins d'animation, le socle (tokens.css) fige tout : les skeletons deviennent un fond uni sans reflet ni pulse, et le spinner passe a une opacite lente au lieu de tourner. Rien a cabler cote app."
      >
        <p className="text-[0.8125rem] text-ink-500">
          Active "Reduire les animations" dans les reglages de ton systeme, puis
          reviens : les placeholders des sections ci-dessus se figent.
        </p>
      </Demo>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ecran                                                                */
/* ------------------------------------------------------------------ */

export function UiLabScreen(): JSX.Element {
  /*
   * L'ETAT D'ECRAN DES VUES NATIVES.
   *
   * `useScreen` est appele ICI, dans le fichier d'ecran, jamais cache dans un
   * contexte : quand une vue se comporte mal, on lit l'etat la ou il est cree.
   * Il possede la requete (recherche, pastille, tri, fenetre, SECTION), la vue
   * active, l'element ouvert, le curseur de fiche et l'ecriture optimiste.
   * L'app, elle, ne possede que le chargeur et les bindings (voir
   * demo-views.ts).
   *
   * Changer de vue n'est qu'un setState ici : la recherche, le tri et la
   * position de defilement survivent par construction, aucune vue ne refiltre
   * ni ne recharge.
   */
  const screen = useScreen<DemoIntervention>({
    appId: DEMO_APP_ID,
    screenId: DEMO_SCREEN_ID,
    /*
     * Chargeur de DEMONSTRATION, en memoire : aucun SDK (voir demo-data.ts).
     *
     * Il ne lit PAS `query.section`, a la difference du modele du gabarit
     * (InventaireScreen, ou le menu segmente une seule collection) : ici le
     * menu change le sujet de la page, et une seule de ses entrees affiche des
     * donnees. Rendre un jeu vide hors de la section `vues` serait faux deux
     * fois : ca ne cacherait rien de plus a l'ecran (les autres sections ne
     * montent pas le ViewHost), et ca viderait le compteur du menu, qui doit
     * dire la meme verite depuis n'importe quelle section. Le core rappelle
     * quand meme ce chargeur a chaque changement de section, puisqu'elle fait
     * partie de sa cle de chargement : sur un decor en memoire, c'est une copie
     * de tableau, pas une requete.
     */
    load: () => listDemoInterventions(),
    getId: (item) => item.id,
    // Entree a froid (reprise de session sur une fiche) : on charge l'element
    // seul plutot que toute la collection.
    get: (id) => getDemoIntervention(id),
    types: DEMO_VIEW_TYPES,
    bindings: DEMO_BINDINGS,
    filters: DEMO_FILTERS,
    defaultFilterId: 'toutes',
    defaultView: 'core.liste',
    // La vitrine s'ouvre sur les vues, ce qu'elle a de plus a raconter. Le
    // socle garde ensuite la section dans la requete, donc dans l'adresse :
    // `?s=motion` renvoie un collegue exactement sur la demo qu'on lui montre.
    defaultSection: 'vues',
  });

  /*
   * LA SECTION AFFICHEE, resolue contre le catalogue du menu.
   *
   * Elle vient du socle, donc de l'adresse : n'importe quelle chaine peut y
   * arriver (`?s=nimportequoi`). Le modele du gabarit se contente d'un
   * transtypage parce que son chargeur segmente : chez lui, une section
   * inconnue rend une liste vide et l'etat vide du gabarit le dit. Ici aucun
   * chargeur ne peut le dire, et le dispatch de contenu plus bas tomberait sur
   * sa derniere branche (Motion) pendant que le menu n'allumerait aucune
   * entree : le contenu mentirait sur l'entree active. On RESOUT donc la
   * section contre `MENU_ITEMS`, avec repli sur la section de depart. Effet de
   * bord bienvenu : le type local `LabSection` sort de la recherche, sans
   * transtypage a expliquer.
   */
  const section: LabSection =
    MENU_ITEMS.find((item) => item.id === screen.section)?.id ?? 'vues';

  const showViews = section === 'vues';

  // Les archives ne sont jamais montrees par les vues : les stats disent la
  // meme verite que la zone 5.
  const liveRecords = useMemo(
    () => screen.records.filter((item) => item.archivedAt == null),
    [screen.records],
  );

  // Compteur du menu : calcule des donnees reelles, jamais en dur.
  const menuItems = MENU_ITEMS.map((item) =>
    item.id === 'vues' ? { ...item, count: liveRecords.length } : item,
  );

  let content: ReactNode;
  if (showViews) {
    /* Zone 5 pilotee par une VUE : le ViewHost monte la vue active, tient les
       etats standard (erreur > chargement > fiche introuvable > vide), la
       memoire de defilement par vue et le filet anti-plantage. L'ecran ne passe
       donc NI loading, NI error, NI empty au gabarit. */
    content = (
      <ViewHost
        state={screen}
        emptyTitle="Aucune intervention"
        emptyHint="Le jeu de demonstration est vide : recharge la page."
      />
    );
  } else if (section === 'primitives') content = <PrimitivesSection />;
  else if (section === 'composes') content = <ComposesSection />;
  else if (section === 'etats') content = <EtatsSection />;
  else content = <MotionSection />;

  return (
    <ScreenLayout
      /* Zone 1 : identite (icone + nom) et menu de sections */
      appName="UI Lab"
      appIcon={<IconSparkle size={18} />}
      menuItems={menuItems}
      activeMenuId={section}
      /* La section part au SOCLE : il la met dans l'adresse et rappelle le
         chargeur. Elle passe par le catalogue du menu, seul endroit qui sache
         quelles sections existent, donc un id inconnu n'entre jamais dans la
         requete. Effet du contrat a connaitre ici : changer de section REFERME
         la fiche ouverte, donc revenir sur `Vues` retrouve la collection,
         jamais une fiche restee ouverte derriere une demo de skeletons. */
      onMenuSelect={(id) => {
        const next = MENU_ITEMS.find((item) => item.id === id);
        if (next != null) screen.setSection(next.id);
      }}
      /* Zone 2 : repli du h1 (le label de l'onglet actif prime) */
      title="UI Lab"
      /* Zone 3 : stats de la section Vues, calculees des donnees reelles. */
      headerStats={
        showViews
          ? [
              { value: String(liveRecords.length), label: 'interventions' },
              {
                value: String(liveRecords.filter((item) => item.status === 'en-cours').length),
                label: 'en cours',
              },
            ]
          : []
      }
      /* Zone 4 : a gauche le groupe defilant (recherche + pastilles), a droite
         le groupe EPINGLE (le selecteur de vues). Les autres sections de la
         vitrine ne listent rien : la rangee entiere disparait. */
      onSearchChange={showViews ? screen.setSearch : undefined}
      searchValue={showViews ? screen.query.search : undefined}
      searchPlaceholder="Rechercher une intervention"
      filters={showViews ? screen.filters : []}
      activeFilterId={showViews ? screen.activeFilterId : undefined}
      onFilterSelect={showViews ? screen.setFilter : undefined}
      views={showViews ? screen.views : []}
      activeViewId={showViews ? screen.viewId : undefined}
      onViewSelect={showViews ? screen.setView : undefined}
      /* Zone 4, groupe de GAUCHE au niveau fiche : la puce de retour (qui porte
         aussi `Echap`) puis l'indicateur d'enregistrement. Elles suivent
         `showViews` comme le reste du selecteur : sans vues, il n'y a pas de
         niveau `record` a deduire, donc rien a rendre. */
      onRecordExit={showViews ? () => screen.openRecord(null) : undefined}
      recordExitLabel={
        showViews
          ? screen.views.find((view) => view.id === screen.lastCollectionViewId)?.label
          : undefined
      }
      saveState={showViews ? screen.saveState : undefined}
      onRetrySave={showViews ? screen.retrySave : undefined}
      /* Groupe EPINGLE au niveau fiche : le curseur prend la place du selecteur
         de vues, et suit `showViews` pour la meme raison. */
      recordCursor={showViews ? screen.cursor : undefined}
      onRecordNavigate={showViews ? screen.openRecord : undefined}
      /* La zone 5 est PRETEE au ViewHost (memoire de defilement) et aux vues. */
      scrollRef={screen.scrollRef}
      /* Zone 6 : les actions appartiennent a l'entree de menu ACTIVE. Aucune
         action lime ici : cette app ne cree rien. */
      quickActions={
        showViews
          ? [
              {
                id: 'reload',
                label: 'Recharger le jeu',
                disabled: screen.status.loading,
                onClick: () => screen.reload(),
              },
            ]
          : []
      }
      /* Zone 6, suite : les actions de la VUE active (`ViewType.actions`),
         rendues par le core sous celles de l'ecran et TOUJOURS en secondaire.
         Aucune des vues montees ici n'en declare : le tableau reste vide, le
         bloc ne bouge pas. C'est la seule porte d'une vue vers le rail. */
      viewActions={showViews ? screen.viewActions : []}
    >
      {/* Zone 5 : la vitrine, a plat, separee par des filets. Les quatre
          sections d'etats ne chargent aucune donnee, elles METTENT EN SCENE
          les etats ; la section Vues, elle, delegue tout au ViewHost. */}
      {content}
    </ScreenLayout>
  );
}

export default UiLabScreen;
